import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ImageWithFallback from '../components/ImageWithFallback';
import { Orbs, CornerAccents } from '../components/DecorativeElements';
import { preloadImages } from '../utils/imagePreloader';
import './Services.css';

const Services = () => {
  const [ref, inView] = useInView({
    threshold: 0.01,
    triggerOnce: true,
    rootMargin: '600px', // Start loading 600px before entering viewport
  });

  const services = [
    {
      id: 'performance-marketing',
      title: 'Performance Marketing',
      description: 'Elevate your brand with precision-driven performance marketing solutions. We optimize your campaigns for maximum ROI, focusing on measurable results and data-driven strategies that deliver exceptional returns on your marketing investment.',
      tagline: 'Elevate your brand with precision-driven performance marketing solutions.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop&q=75&auto=format',
      icon: '📈',
      features: [
        'Campaign Optimization & Management',
        'ROI-Focused Marketing Strategies',
        'Data-Driven Performance Analysis',
        'Multi-Channel Campaign Execution',
        'Conversion Rate Optimization'
      ]
    },
    {
      id: 'digital-marketing-analytics',
      title: 'Digital Marketing & Dashboard Analytics',
      description: 'Crafting connections, analyzing impact: your Digital success story, at your fingertips. We combine strategic digital marketing with comprehensive analytics dashboards to track, measure, and optimize your online presence.',
      tagline: 'Crafting connections, analyzing impact: your Digital success story, at your fingertips.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop&q=75&auto=format',
      icon: '📊',
      features: [
        'Digital Marketing Strategy',
        'Real-Time Analytics Dashboards',
        'Social Media Management',
        'SEO & SEM Optimization',
        'Performance Tracking & Reporting'
      ]
    },
    {
      id: 'web-app-development',
      title: 'Web & App Development',
      description: 'Custom web and app solutions tailored to your business needs. We build your digital dreams into reality with cutting-edge technologies, responsive designs, and scalable architectures that grow with your business.',
      tagline: 'Custom web and app solutions tailored to your business needs. We build your digital dreams into reality.',
      image: 'https://www.creative-tim.com/blog/content/images/2022/01/which-development-job-is-right-for-you.jpg',
      icon: '🌐',
      features: [
        'Custom Web Applications',
        'Mobile App Development',
        'E-commerce Solutions',
        'Responsive Website Design',
        'API Development & Integration'
      ]
    },
    {
      id: 'graphic-designing',
      title: 'Graphic Designing',
      description: 'Where creativity meets clarity: crafting compelling visuals that speak volumes. Our design team creates stunning visual identities, marketing materials, and digital assets that capture attention and communicate your brand effectively.',
      tagline: 'Where creativity meets clarity: crafting compelling visuals that speak volumes.',
      image: 'https://t4.ftcdn.net/jpg/03/06/44/55/360_F_306445529_dFXbKcu8oAYESRfm7MFujNqAs89tc186.jpg',
      icon: '🎨',
      features: [
        'Brand Identity Design',
        'Marketing Collateral',
        'Digital Graphics & Illustrations',
        'Logo Design & Branding',
        'Print & Digital Media Design'
      ]
    },
    {
      id: 'consulting-services',
      title: 'Consulting Services',
      description: 'Transforming footage into captivating stories, and your channel into a digital sensation. We provide strategic consulting to help businesses navigate digital transformation, optimize operations, and achieve their growth objectives.',
      tagline: 'Strategic consulting to transform your business and drive digital success.',
      image: 'https://media.istockphoto.com/id/1445807259/photo/business-people-doing-calculations-analyze-the-work-at-the-meeting.jpg?s=612x612&w=0&k=20&c=I1iGLJ15sDfGI13KpPV_Vmpyu9fRYcfdY7187E2gRhc=',
      icon: '💼',
      features: [
        'Business Strategy Consulting',
        'Digital Transformation Advisory',
        'Process Optimization',
        'Technology Consulting',
        'Growth Strategy & Planning'
      ]
    },
    {
      id: 'cyber-security',
      title: 'Cyber Security',
      description: 'Guardians of your digital realm: fortifying your defenses in an ever-evolving cyber landscape. We provide comprehensive cybersecurity solutions to protect your business from threats and ensure compliance with industry standards.',
      tagline: 'Guardians of your digital realm: fortifying your defenses in an ever-evolving cyber landscape.',
      image: 'https://t4.ftcdn.net/jpg/02/45/63/69/360_F_245636933_kY23ohGptK5t6n8wGSXIgLgVXWeHJRct.jpg',
      icon: '🔒',
      features: [
        'Security Audits & Assessments',
        'Threat Detection & Prevention',
        'Compliance & Risk Management',
        'Security Infrastructure Setup',
        'Incident Response & Recovery'
      ]
    },
    {
      id: 'digital-strategy',
      title: 'Digital Strategy',
      description: 'Comprehensive strategies for web development, Digital management, SEO, and more. We develop holistic digital strategies that align with your business goals and drive measurable results across all digital channels.',
      tagline: 'Comprehensive strategies for web development, Digital management, SEO, and more.',
      image: 'https://media.istockphoto.com/id/1072111648/photo/digital-transformation-technology-strategy-digitization-and-digitalization-of-business.jpg?s=612x612&w=0&k=20&c=sotCQK1GMImT_hrUq2CM6fyYaqvPlZ2lS_R1Y3h4f1g=',
      icon: '📱',
      features: [
        'Digital Strategy Development',
        'SEO & Content Strategy',
        'Social Media Strategy',
        'Digital Channel Management',
        'Performance Measurement & Optimization'
      ]
    },
    {
      id: 'content-production',
      title: 'Content Production',
      description: 'From copywriting to press releases, we craft content that resonates with your audience. Our content production services help you tell your story effectively and engage your target audience across all platforms.',
      tagline: 'From copywriting to press releases, we craft content that resonates with your audience.',
      image: 'https://media.istockphoto.com/id/1294781336/photo/creative-concept-creator-visual-contents.jpg?s=612x612&w=0&k=20&c=ZMHizVr9goyIU88cJinkwr-H8rXSUN_Lp_G_XQFkNYc=',
      icon: '✍️',
      features: [
        'Copywriting & Content Writing',
        'Press Releases & Media Content',
        'Blog & Article Writing',
        'Video Content Production',
        'Content Strategy & Planning'
      ]
    },
    {
      id: 'legal-services',
      title: 'Legal Services',
      description: 'Providing comprehensive legal services to protect your business and ensure compliance. We offer legal support for contracts, compliance, intellectual property, and business law to keep your operations secure and legally sound.',
      tagline: 'Providing comprehensive legal services to protect your business and ensure compliance.',
      image: 'https://blog.ipleaders.in/wp-content/uploads/2020/11/LEGAL_gavel-scale-book_10-5-2018-1.jpg',
      icon: '⚖️',
      features: [
        'Contract Review & Drafting',
        'Compliance & Regulatory Support',
        'Intellectual Property Protection',
        'Business Law Advisory',
        'Legal Documentation & Filing'
      ]
    },
    {
      id: 'generative-ai',
      title: 'Generative AI',
      description: 'Leverage cutting-edge AI to automate processes, generate creative content, and unlock valuable insights from your data. Our Generative AI solutions help businesses innovate, automate, and transform their operations.',
      tagline: 'Leverage cutting-edge AI to automate processes, generate creative content, and unlock valuable insights.',
      image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT0AVCYMw2QEZpDAbaQSVVQI4vFY0zJvI56KQ&s',
      icon: '🤖',
          features: [
            'AI-Powered Automation',
            'Content Generation & Creation',
            'Data Insights & Analytics',
            'Machine Learning Solutions',
            'AI Strategy & Implementation',
            'Agentic Solutions'
          ]
    },
  ];

  // Preload first few service images immediately when component mounts
  useEffect(() => {
    const priorityImages = services.slice(0, 8).map(service => service.image);
    preloadImages(priorityImages, {
      priority: 'high',
      stagger: false
    }).catch(() => {});
  }, []);

  const placeholderImage = (icon) => {
    const svg = encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="800" height="500" viewBox="0 0 800 500">
        <rect width="800" height="500" fill="#f8f9fa"/>
        <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-family="Arial" font-size="120" fill="#ddd">${icon}</text>
        <rect width="800" height="500" fill="none" stroke="#e5e5e5" stroke-width="2"/>
      </svg>
    `);
    return `data:image/svg+xml,${svg}`;
  };

  return (
    <div className="services-page">
      <Orbs />
      <CornerAccents />
      <section className="services-hero">
        <div className="container">
          <motion.h1
            className="page-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our Services
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Check out our comprehensive range of services designed to drive your digital success
          </motion.p>
        </div>
      </section>

      <section className="services-content" ref={ref}>
        <div className="container">
          {services.map((service, index) => (
            <motion.section
              key={service.id}
              id={service.id}
              className="service-section-detailed"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.08 }}
            >
              <div className={`service-section-content ${index % 2 === 0 ? 'left-image' : 'right-image'}`}>
                <div className="service-section-image">
                  <ImageWithFallback
                    src={service.image}
                    alt={service.title}
                    className="service-detailed-image"
                    priority={index < 3}
                    fallback={placeholderImage(service.icon)}
                  />
                </div>
                <div className="service-section-text">
                  <div className="service-section-number">{String(index + 1).padStart(2, '0')}</div>
                  <h2 className="service-section-title">{service.title}</h2>
                  <p className="service-section-tagline">{service.tagline}</p>
                  <p className="service-section-description">{service.description}</p>
                  <div className="service-features">
                    <h3>What We Offer:</h3>
                    <ul>
                      {service.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.section>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Services;
